/**
 * Options Analysis Page
 * Comprehensive options trading analysis with PCR, Greeks, IV
 *
 * Version: 6.2.0
 */
import React, { useState, useEffect } from 'react'
import { TrendingUp, TrendingDown, Activity, Gauge, Target, Zap } from 'lucide-react'
import './OptionsAnalysis.css'

// Types
interface GreeksData {
  delta: number
  gamma: number
  theta: number
  vega: number
  rho: number
}

interface OptionsData {
  symbol: string
  pcr: number
  iv: number
  ivRank: number
  maxPain: number
  greeks: GreeksData
  ltp: number
  change: number
}

const OptionsAnalysis: React.FC = () => {
  const [symbol, setSymbol] = useState('NIFTY')
  const [expiry, setExpiry] = useState('weekly')
  const [data, setData] = useState<OptionsData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    const timer = setTimeout(() => {
      setData({
        symbol: symbol,
        pcr: 0.87,
        iv: 18.5,
        ivRank: 42,
        maxPain: 24500,
        ltp: 24456.75,
        change: 1.23,
        greeks: {
          delta: 0.52,
          gamma: 0.0023,
          theta: -12.45,
          vega: 0.1845,
          rho: 0.0892
        }
      })
      setLoading(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [symbol, expiry])

  const getPCRStatus = (pcr: number) => {
    if (pcr > 1.2) return { status: 'Bullish', color: 'success', icon: TrendingUp }
    if (pcr < 0.8) return { status: 'Bearish', color: 'danger', icon: TrendingDown }
    return { status: 'Neutral', color: 'warning', icon: Activity }
  }

  const pcrInfo = data ? getPCRStatus(data.pcr) : null
  const PCRIcon = pcrInfo?.icon || Activity

  return (
    <div className="options-analysis-page">
      <div className="page-header">
        <div className="header-content">
          <h1><Activity className="page-icon" /> Options Analysis</h1>
          <p className="page-subtitle">Comprehensive options metrics and Greeks analysis</p>
        </div>
        <div className="header-actions">
          <select 
            value={symbol} 
            onChange={(e) => setSymbol(e.target.value)}
            className="symbol-select"
          >
            <option value="NIFTY">NIFTY</option>
            <option value="BANKNIFTY">BANKNIFTY</option>
            <option value="FINNIFTY">FINNIFTY</option>
          </select>
          <select 
            value={expiry} 
            onChange={(e) => setExpiry(e.target.value)}
            className="expiry-select"
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : data ? (
        <div className="options-grid">
          {/* PCR Card */}
          <div className={`metric-card pcr-card ${pcrInfo?.color}`}>
            <div className="card-header">
              <span className="metric-label">Put-Call Ratio</span>
              <PCRIcon className="metric-icon" size={20} />
            </div>
            <div className="metric-value">{data.pcr.toFixed(2)}</div>
            <div className={`metric-status ${pcrInfo?.color}`}>
              {pcrInfo?.status}
            </div>
            <div className="metric-description">
              Above 1.2 = Bullish signal
            </div>
          </div>

          {/* IV Card */}
          <div className="metric-card iv-card">
            <div className="card-header">
              <span className="metric-label">Implied Volatility</span>
              <Gauge className="metric-icon" size={20} />
            </div>
            <div className="metric-value">{data.iv.toFixed(1)}%</div>
            <div className="metric-sub">Rank: {data.ivRank}%</div>
          </div>

          {/* Max Pain Card */}
          <div className="metric-card pain-card">
            <div className="card-header">
              <span className="metric-label">Max Pain</span>
              <Target className="metric-icon" size={20} />
            </div>
            <div className="metric-value">{data.maxPain.toLocaleString()}</div>
            <div className="metric-sub">Strike with max loss</div>
          </div>

          {/* LTP Card */}
          <div className={`metric-card ltp-card ${data.change >= 0 ? 'up' : 'down'}`}>
            <div className="card-header">
              <span className="metric-label">Last Price</span>
              {data.change >= 0 ? <TrendingUp className="metric-icon" size={20} /> : <TrendingDown className="metric-icon" size={20} />}
            </div>
            <div className="metric-value">{data.ltp.toLocaleString()}</div>
            <div className={`metric-change ${data.change >= 0 ? 'up' : 'down'}`}>
              {data.change >= 0 ? '+' : ''}{data.change}%
            </div>
          </div>

          {/* Greeks Card */}
          <div className="greeks-card">
            <div className="card-header">
              <span className="metric-label"><Zap /> Greeks</span>
            </div>
            <div className="greeks-grid">
              <div className="greek-item">
                <span className="greek-label">Delta</span>
                <span className="greek-value">{data.greeks.delta.toFixed(4)}</span>
                <span className="greek-desc">Price sensitivity</span>
              </div>
              <div className="greek-item">
                <span className="greek-label">Gamma</span>
                <span className="greek-value">{data.greeks.gamma.toFixed(4)}</span>
                <span className="greek-desc">Delta change rate</span>
              </div>
              <div className="greek-item">
                <span className="greek-label">Theta</span>
                <span className="greek-value negative">{data.greeks.theta.toFixed(2)}</span>
                <span className="greek-desc">Time decay/day</span>
              </div>
              <div className="greek-item">
                <span className="greek-label">Vega</span>
                <span className="greek-value">{data.greeks.vega.toFixed(4)}</span>
                <span className="greek-desc">Volatility impact</span>
              </div>
              <div className="greek-item">
                <span className="greek-label">Rho</span>
                <span className="greek-value">{data.greeks.rho.toFixed(4)}</span>
                <span className="greek-desc">Interest rate impact</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default OptionsAnalysis
